/**
 * BlogAgentPage — Blog Agent is a 2-stage flow:
 *   1. "start" → AI proposes 25 SEO-optimised titles grouped by category.
 *   2. "approve" → user edits and approves titles; daily generation begins.
 * The page then shows agent status + already-generated articles.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  BookOpen, Loader2, Sparkles, CheckCircle2, Plus, Clock, FileText,
} from "lucide-react";
import { ToolHeader, SignInGate } from "./CompetitorSpyPage";

export default function BlogAgentPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null); // full detail
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [form, setForm] = useState({ url: "", niche: "" });

  useEffect(() => { if (user) fetchAgents(); }, [user]);  // eslint-disable-line
  const fetchAgents = async () => {
    const r = await axios.get(`${API}/blog-agent/status`, { params: { email: user.email } }).catch(() => null);
    const l = r?.data?.agents || [];
    setAgents(l);
    if (l.length && !selected) loadAgent(l[0].agent_id);
  };
  const loadAgent = async (id) => {
    setLoading(true);
    const r = await axios.get(`${API}/blog-agent/${id}`).catch(() => null);
    setSelected(r?.data || null);
    setLoading(false);
  };
  const handleStart = async (e) => {
    e.preventDefault();
    if (!form.url) return;
    setStarting(true);
    const r = await axios.post(`${API}/blog-agent/start`, {
      url: form.url, email: user.email, language: language || "en", niche: form.niche,
    }).catch(() => null);
    if (r?.data?.agent_id) {
      setForm({ url: "", niche: "" });
      await fetchAgents();
      await loadAgent(r.data.agent_id);
    }
    setStarting(false);
  };
  const handleApprove = async (titles) => {
    if (!selected) return;
    setApproving(true);
    await axios.post(`${API}/blog-agent/${selected.agent_id}/approve`, { titles }).catch(() => null);
    await loadAgent(selected.agent_id);
    await fetchAgents();
    setApproving(false);
  };

  if (!user) return <SignInGate title={t.ba_title || "Blog Agent"} sub={t.ba_sign_in_desc || "Sign in to let AI plan and publish a 25-article blog for you."} />;

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <ToolHeader />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-6 h-6 text-[#a855f7]" />
          <h1 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="ba-title">{t.ba_title || "AI Blog Agent"}</h1>
          <span className="px-2 py-0.5 bg-[#a855f7]/15 text-[#c084fc] text-xs font-bold">€25</span>
        </div>
        <p className="text-[var(--color-text-muted)] mb-8">{t.ba_desc || "AI proposes 25 SEO-optimised article titles. You approve — it writes one per day for 25 days straight."}</p>

        {/* Start form (always visible, except when an agent is currently pending_approval or active) */}
        {!selected && (
          <form onSubmit={handleStart} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 mb-8" data-testid="ba-start-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.ba_url || "Website URL *"}</label>
                <input required value={form.url} onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                  placeholder="https://yoursite.com" data-testid="ba-url"
                  className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.ba_niche || "Niche (optional — auto-detect)"}</label>
                <input value={form.niche} onChange={(e) => setForm((p) => ({ ...p, niche: e.target.value }))}
                  placeholder="e.g. SEO for small businesses" data-testid="ba-niche"
                  className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" />
              </div>
            </div>
            <button type="submit" disabled={starting || !form.url} data-testid="ba-start-btn"
              className="mt-5 w-full md:w-auto flex items-center gap-2 px-6 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50">
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {starting ? (t.ba_generating_titles || "Generating 25 titles…") : (t.ba_propose || "Propose 25 titles")}
            </button>
          </form>
        )}

        {agents.length > 0 && (
          <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
            <button onClick={() => setSelected(null)} data-testid="ba-new-btn"
              className="px-4 py-2 border border-dashed border-[#a855f7]/50 text-[#c084fc] text-sm font-bold font-['Cabinet_Grotesk'] whitespace-nowrap flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> {t.ba_new_agent || "New agent"}
            </button>
            {agents.map((a) => (
              <button key={a.agent_id} data-testid={`ba-agent-${a.agent_id}`} onClick={() => loadAgent(a.agent_id)}
                className={`px-4 py-2 border whitespace-nowrap font-bold font-['Cabinet_Grotesk'] text-sm ${selected?.agent_id === a.agent_id ? "bg-[var(--color-surface)] border-[#a855f7]" : "bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
                {a.url.replace(/^https?:\/\//, "")} · <span className="text-[#c084fc]">{a.status}</span>
              </button>
            ))}
          </div>
        )}

        {loading && <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#a855f7] mx-auto" /></div>}

        {selected?.status === "pending_approval" && (
          <PendingApproval agent={selected} onApprove={handleApprove} approving={approving} t={t} />
        )}
        {selected?.status === "active" && <ActiveAgent agent={selected} t={t} />}
        {selected?.status === "completed" && <ActiveAgent agent={selected} t={t} />}
      </div>
    </div>
  );
}

function PendingApproval({ agent, onApprove, approving, t }) {
  const [titles, setTitles] = useState(agent.proposed_titles || []);
  // Keep local titles in sync when the user switches between agents that are
  // both in pending_approval state — otherwise the list would stay stuck on
  // the first agent's proposals.
  useEffect(() => { setTitles(agent.proposed_titles || []); }, [agent.agent_id]);  // eslint-disable-line
  const updateTitle = (i, val) => setTitles((p) => p.map((x, idx) => (idx === i ? { ...x, title: val } : x)));
  const removeTitle = (i) => setTitles((p) => p.filter((_, idx) => idx !== i));

  return (
    <div data-testid="ba-pending-approval">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black font-['Cabinet_Grotesk']">{t.ba_review || "Review & approve titles"}</h2>
        <span className="text-sm text-[var(--color-text-muted)]">{titles.length} / {agent.proposed_titles?.length || 25}</span>
      </div>
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] divide-y divide-[var(--color-border)] mb-6" data-testid="ba-title-list">
        {titles.map((t_, i) => (
          <div key={i} className="flex items-center gap-3 p-3" data-testid={`ba-title-${i}`}>
            <span className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] w-8 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <input value={t_.title} onChange={(e) => updateTitle(i, e.target.value)}
              className="flex-1 bg-transparent text-sm focus:outline-none" />
            <span className="text-[10px] px-2 py-0.5 bg-[#a855f7]/10 text-[#c084fc] font-bold">{t_.category || "General"}</span>
            <span className="text-[10px] text-[var(--color-text-muted)] font-['JetBrains_Mono']">{t_.search_volume || 0}/mo</span>
            <button onClick={() => removeTitle(i)} className="text-[var(--color-text-muted)] hover:text-red-500 text-xs" data-testid={`ba-remove-${i}`}>✕</button>
          </div>
        ))}
      </div>
      <button onClick={() => onApprove(titles)} disabled={approving || !titles.length} data-testid="ba-approve-btn"
        className="w-full md:w-auto flex items-center gap-2 px-8 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50">
        {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        {t.ba_approve || "Approve — start daily writing"}
      </button>
    </div>
  );
}

function ActiveAgent({ agent, t }) {
  const total = agent.total_days || agent.approved_titles?.length || 25;
  const day = agent.current_day || 0;
  const pct = Math.round((day / total) * 100);
  return (
    <div data-testid="ba-active">
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-['JetBrains_Mono'] uppercase text-[var(--color-text-muted)] tracking-wider">{t.ba_progress || "Progress"}</span>
          <span className="text-sm font-bold">{day} / {total} · {pct}%</span>
        </div>
        <div className="h-2 bg-[var(--color-bg)] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#a855f7] to-[#7c3aed] transition-all" style={{ width: `${pct}%` }} />
        </div>
        {agent.next_generation && (
          <div className="flex items-center gap-2 mt-4 text-sm text-[var(--color-text-muted)]">
            <Clock className="w-4 h-4" /> {t.ba_next_gen || "Next article"}: {new Date(agent.next_generation).toLocaleString()}
          </div>
        )}
      </div>

      {agent.articles?.length > 0 && (
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="ba-articles">
          <h3 className="text-sm font-bold text-[#FFE600] uppercase tracking-wider mb-4 font-['JetBrains_Mono']">{t.ba_published || "Published so far"}</h3>
          <div className="space-y-2">
            {agent.articles.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[var(--color-bg)] border border-[var(--color-border)]" data-testid={`ba-article-${i}`}>
                <FileText className="w-4 h-4 text-[#c084fc] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{a.title}</div>
                  <div className="text-xs text-[var(--color-text-muted)]">{a.target_keyword} · {a.status || "published"}</div>
                </div>
                {a.slug && <a href={`/blog/${a.slug}`} className="text-xs text-[#c084fc] font-bold">View →</a>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
